import LogoExamples from "../components/shared/brand/logo-examples";
import { StopButton } from "../components/shared/buttons/stop-button";
import { SubmitButton } from "../components/shared/buttons/submit-button";
import Card from "../components/shared/card";
import Input from "../components/shared/input";
import TamaguiExample from "../components/shared/tamagui-example";
import Typography from "../components/shared/typography";
import MedicalRecordRequestStatusTracker from "../medical-record-requests/components/MedicalRecordRequestStatusTracker";
import { useState } from "react";

export const ComponentsShowcase = () => {
  const [demoStatusIndex, setDemoStatusIndex] = useState(0);
  const demoStatuses = [
    "pending",
    "ready_to_review",
    "lookup_in_progress",
    "ready_to_send",
    "fax_sending",
    "department_contacted",
    "completed",
  ] as const;
  const demoRequest = {
    id: "demo-request",
    status: demoStatuses[demoStatusIndex],
    patientId: "PAT-123",
    patientName: "Demo Patient",
    createdAt: new Date().toISOString(),
    pdfUrl: null,
    hasSignatureImage: false,
  } as any;

  const advanceStatus = () => {
    setDemoStatusIndex((idx) => (idx + 1) % demoStatuses.length);
  };

  const resetStatus = () => setDemoStatusIndex(0);

  return (
    <div className="container mx-auto px-4 py-8">
      <Typography variant="h2" className="mb-8 text-terracotta">
        Component Showcase
      </Typography>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        <Card className="w-full" variant="default" elevation={1}>
          <Card.Header>
            <Typography variant="h4" className="text-terracotta">
              Logo Components
            </Typography>
          </Card.Header>
          <Card.Body>
            <LogoExamples />
          </Card.Body>
        </Card>

        <Card className="w-full" variant="cream" elevation={1}>
          <Card.Header variant="cream">
            <Typography variant="h4" className="text-terracotta">
              Button Components
            </Typography>
          </Card.Header>
          <Card.Body>
            <div className="space-y-6">
              <div>
                <Typography variant="h5" className="mb-4 text-emerald-700">
                  Submit Buttons
                </Typography>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <SubmitButton onClick={() => {}} color="light">
                    Light Button
                  </SubmitButton>
                  <SubmitButton onClick={() => {}} color="default">
                    Default Button
                  </SubmitButton>
                  <SubmitButton onClick={() => {}} iconOnly />
                </div>
              </div>

              <div>
                <Typography variant="h5" className="mb-4 text-emerald-700">
                  Stop Buttons
                </Typography>
                <div className="flex space-x-4">
                  <StopButton onClick={() => {}} />
                  <StopButton onClick={() => {}} isDisabled />
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      <Card
        className="w-full mb-12"
        variant="terracotta"
        elevation={1}
        hoverEffect
      >
        <Card.Header variant="terracotta">
          <Typography variant="h4" className="text-purple-800">
            Input Components
          </Typography>
        </Card.Header>
        <Card.Body className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            id="basic-input"
            label="Basic Input"
            placeholder="Type something..."
            fullWidth
          />

          <Input
            id="input-with-helper"
            label="Input with Helper Text"
            helperText="This is a helper text"
            placeholder="Type something..."
            fullWidth
          />

          <Input
            id="input-with-error"
            label="Input with Error"
            error="This field is required"
            placeholder="Type something..."
            fullWidth
          />

          <Input
            id="disabled-input"
            label="Disabled Input"
            placeholder="Type something..."
            disabled
            fullWidth
          />
        </Card.Body>
      </Card>

      <Card className="w-full" variant="default" elevation={1}>
        <Card.Header variant="default">
          <Typography variant="h4" className="text-terracotta">
            Typography Components
          </Typography>
        </Card.Header>
        <Card.Body>
          <div className="space-y-4">
            <Typography variant="h1">Heading 1</Typography>
            <Typography variant="h2">Heading 2</Typography>
            <Typography variant="h3">Heading 3</Typography>
            <Typography variant="h4">Heading 4</Typography>
            <Typography variant="h5">Heading 5</Typography>
            <Typography variant="h6">Heading 6</Typography>
            <Typography variant="subtitle1">Subtitle 1</Typography>
            <Typography variant="subtitle2">Subtitle 2</Typography>
            <Typography variant="body1">
              Body 1: The quick brown fox jumps over the lazy dog.
            </Typography>
            <Typography variant="body2">
              Body 2: The quick brown fox jumps over the lazy dog.
            </Typography>
            <Typography variant="caption">
              Caption: The quick brown fox jumps over the lazy dog.
            </Typography>
            <Typography variant="button">Button text</Typography>
            <Typography variant="overline">Overline text</Typography>
          </div>
        </Card.Body>
      </Card>

      <Card className="w-full mt-12" variant="default" elevation={1}>
        <Card.Header variant="default" className="flex flex-wrap items-center gap-3 justify-between">
          <Typography variant="h4" className="text-terracotta">
            Status Tracker (Demo)
          </Typography>
          <div className="flex flex-wrap gap-2">
            <SubmitButton size="sm" hideArrow onClick={advanceStatus}>
              Next status
            </SubmitButton>
            <SubmitButton size="sm" hideArrow color="light" onClick={resetStatus}>
              Reset
            </SubmitButton>
          </div>
        </Card.Header>
        <Card.Body>
          <MedicalRecordRequestStatusTracker request={demoRequest} />
          <Typography variant="caption" className="mt-2 block text-brown-600">
            Demo statuses (cycling): {demoStatuses.join(" → ")}
          </Typography>
        </Card.Body>
      </Card>

      <div className="mt-12">
        <Typography variant="h2" className="mb-8 text-terracotta">
          Tamagui Styling Example
        </Typography>
        <TamaguiExample />
      </div>
    </div>
  );
};

export default ComponentsShowcase;
